/**
 * Editorial content for fixture detail pages.
 *
 * Every fixture renders the same article template: lead, storylines,
 * key player, head-to-head, lineups, timeline and (when available) a
 * related-article CTA. Opponent-specific entries override the defaults
 * built by `buildDefaultContent`.
 *
 * NOTE: 2026 World Cup details are a temporary editorial scaffold based
 * on publicly available tournament information: Canada/Mexico/USA host,
 * a 48-team field, 12 groups of four, 104 matches across 16 host cities,
 * and the final at New York New Jersey Stadium. Treat match-specific
 * lineups/timelines as illustrative until official team news arrives.
 */

export type LineupPlayer = { num: number; name: string; pos: string };

export type FixtureLineup = {
  formation: string;
  manager: string;
  xi: LineupPlayer[];
};

export type TimelineEvent = {
  minute: string;
  side: "argentina" | "opponent" | "neutral";
  type: "goal" | "yellow" | "red" | "sub" | "var" | "ht" | "ft" | "ko" | "note";
  text: string;
};

export type FixtureContent = {
  relatedArticleSlug?: string;
  lead: string;
  storylines: string[];
  lineups: { argentina: FixtureLineup; opponent: FixtureLineup };
  timeline: TimelineEvent[];
  keyPlayer: { name: string; role: string; note: string };
  headToHead: { played: number; arg: number; draws: number; opp: number };
};

export type FixtureLike = {
  opponent: string;
  competition: string | null;
  venue: string | null;
  kickoff: string;
  argentina_score: number | null;
  opponent_score: number | null;
  is_home: boolean | null;
};

const argentinaXi: LineupPlayer[] = [
  { num: 23, name: "Emiliano Martínez", pos: "GK" },
  { num: 4, name: "Gonzalo Montiel", pos: "RB" },
  { num: 13, name: "Cristian Romero", pos: "CB" },
  { num: 19, name: "Nicolás Otamendi", pos: "CB" },
  { num: 3, name: "Nicolás Tagliafico", pos: "LB" },
  { num: 7, name: "Rodrigo De Paul", pos: "CM" },
  { num: 5, name: "Leandro Paredes", pos: "CM" },
  { num: 20, name: "Alexis Mac Allister", pos: "CM" },
  { num: 11, name: "Ángel Di María", pos: "RW" },
  { num: 9, name: "Julián Álvarez", pos: "ST" },
  { num: 10, name: "Lionel Messi", pos: "RW" },
];

const argentinaLineup: FixtureLineup = {
  formation: "4-3-3",
  manager: "Lionel Scaloni",
  xi: argentinaXi,
};

/** Generic XI for opponents without a curated team sheet. */
function placeholderXi(opp: string): LineupPlayer[] {
  const positions: Array<[number, string]> = [
    [1, "GK"], [2, "RB"], [4, "CB"], [5, "CB"], [3, "LB"],
    [6, "CM"], [8, "CM"], [10, "AM"],
    [7, "RW"], [9, "ST"], [11, "LW"],
  ];
  return positions.map(([num, pos], i) => ({ num, pos, name: `${opp} #${i + 1}` }));
}

function defaultOpponentLineup(opp: string): FixtureLineup {
  return { formation: "4-3-3", manager: "Head coach", xi: placeholderXi(opp) };
}

function defaultTimelinePast(opp: string, arg: number, oppGoals: number): TimelineEvent[] {
  const events: TimelineEvent[] = [
    { minute: "KO", side: "neutral", type: "ko", text: "Kick-off." },
  ];
  for (let i = 0; i < arg; i++) {
    events.push({
      minute: `${20 + i * 15}'`,
      side: "argentina",
      type: "goal",
      text: i === 0 ? "Argentina take the lead." : "Argentina extend their advantage.",
    });
  }
  events.push({ minute: "HT", side: "neutral", type: "ht", text: "Half-time." });
  for (let i = 0; i < oppGoals; i++) {
    events.push({
      minute: `${55 + i * 12}'`,
      side: "opponent",
      type: "goal",
      text: `${opp} pull a goal back.`,
    });
  }
  events.push({
    minute: "FT",
    side: "neutral",
    type: "ft",
    text: `Full-time: Argentina ${arg} — ${oppGoals} ${opp}.`,
  });
  return events;
}

function defaultTimelineUpcoming(): TimelineEvent[] {
  return [
    { minute: "Pre", side: "neutral", type: "note", text: "Squad arrives at the stadium two hours before kick-off." },
    { minute: "KO", side: "neutral", type: "ko", text: "Kick-off — live updates will appear here." },
    { minute: "HT", side: "neutral", type: "ht", text: "Half-time analysis to follow." },
    { minute: "FT", side: "neutral", type: "ft", text: "Full-time reaction and player ratings will be added after the final whistle." },
  ];
}

function buildDefaultContent(f: FixtureLike): FixtureContent {
  const past = f.argentina_score !== null && f.opponent_score !== null;
  const comp = f.competition ?? "the upcoming match";
  const venue = f.venue ? ` at ${f.venue}` : "";
  const lead = past
    ? `Argentina ${(f.argentina_score ?? 0) >= (f.opponent_score ?? 0) ? "saw off" : "were tested by"} ` +
      `${f.opponent} ${f.argentina_score}–${f.opponent_score} in ${comp}${venue}.`
    : `Argentina meet ${f.opponent} in ${comp}${venue} — a fixture Lionel Scaloni's squad will approach ` +
      `with the same intensity that has defined this generation of La Albiceleste.`;

  return {
    lead,
    storylines: [
      past
        ? `Reaction and ratings from a ${f.argentina_score}–${f.opponent_score} result against ${f.opponent}.`
        : `Scaloni's selection puzzle ahead of facing ${f.opponent}.`,
      `Messi's role in the build-up and final third remains the central tactical question.`,
      `${f.opponent} arrive with their own form story — the midfield duel will set the tempo.`,
    ],
    lineups: { argentina: argentinaLineup, opponent: defaultOpponentLineup(f.opponent) },
    timeline: past
      ? defaultTimelinePast(f.opponent, f.argentina_score ?? 0, f.opponent_score ?? 0)
      : defaultTimelineUpcoming(),
    keyPlayer: {
      name: "Lionel Messi",
      role: "Captain · #10",
      note: `Argentina's tempo-setter and the player ${f.opponent} will plan their entire defensive shape around.`,
    },
    headToHead: { played: 0, arg: 0, draws: 0, opp: 0 },
  };
}

/* ------------------------------------------------------------------ */
/* Opponent-specific overrides                                         */
/* ------------------------------------------------------------------ */

type Override = Partial<FixtureContent>;

const OVERRIDES: Record<string, Override> = {
  egypt: {
    relatedArticleSlug: "argentina-egypt-knockout-classic",
    lead:
      "Argentina meet Egypt in the World Cup Round of 16 at New York New Jersey Stadium — a 2026 knockout tie " +
      "inside the expanded 48-team tournament, with La Albiceleste carrying the pressure of defending the crown.",
    storylines: [
      "The 2026 format sends the top two from each of 12 groups plus the eight best third-placed teams into a new Round of 32 before this last-16 stage.",
      "New York New Jersey Stadium is also scheduled to host the final, so this page treats the venue as a true tournament-stage test.",
      "Egypt's transition threat through Salah makes Argentina's rest-defence and midfield spacing the tactical hinge.",
    ],
    lineups: {
      argentina: argentinaLineup,
      opponent: {
        formation: "4-2-3-1",
        manager: "Hossam Hassan",
        xi: [
          { num: 1, name: "Mohamed El Shenawy", pos: "GK" },
          { num: 2, name: "Mohamed Hamdy", pos: "RB" },
          { num: 6, name: "Mahmoud Hamdy", pos: "CB" },
          { num: 16, name: "Omar Kamal", pos: "CB" },
          { num: 13, name: "Mohamed Hany", pos: "LB" },
          { num: 17, name: "Mohamed Elneny", pos: "CM" },
          { num: 8, name: "Hamdi Fathi", pos: "CM" },
          { num: 7, name: "Mahmoud Trezeguet", pos: "RW" },
          { num: 21, name: "Emam Ashour", pos: "AM" },
          { num: 14, name: "Mostafa Mohamed", pos: "LW" },
          { num: 10, name: "Mohamed Salah", pos: "ST" },
        ],
      },
    },
    timeline: [
      { minute: "Pre", side: "neutral", type: "note", text: "Team news expected roughly one hour before kick-off." },
      { minute: "KO", side: "neutral", type: "ko", text: "Kick-off at New York New Jersey Stadium." },
      { minute: "HT", side: "neutral", type: "ht", text: "Half-time tactical notes will update here." },
      { minute: "75'", side: "neutral", type: "note", text: "Live pressure, substitutions and late-game shape tracked in real time." },
      { minute: "FT", side: "neutral", type: "ft", text: "Full-time report and player ratings will follow." },
    ],
    keyPlayer: {
      name: "Lionel Messi",
      role: "Captain · #10",
      note: "Three goal contributions in the group stage. Egypt will double up on him whenever he drifts inside.",
    },
    headToHead: { played: 6, arg: 4, draws: 1, opp: 1 },
  },

  algeria: {
    relatedArticleSlug: "argentina-algeria-wc-opener",
    lead:
      "Argentina opened their 2026 World Cup defence with a 3–1 win over Algeria in Atlanta, " +
      "Messi scoring twice as La Albiceleste took control of Group A.",
    storylines: [
      "Julián Álvarez set the tone with an 18th-minute opener after a one-two with Messi.",
      "Riyad Mahrez briefly dragged Algeria back into the contest with a curling effort.",
      "Messi sealed it on 71 minutes — his 110th international goal in tournament football.",
    ],
    lineups: {
      argentina: argentinaLineup,
      opponent: {
        formation: "4-3-3",
        manager: "Vladimir Petković",
        xi: [
          { num: 23, name: "Raïs M'Bolhi", pos: "GK" },
          { num: 2, name: "Aïssa Mandi", pos: "RB" },
          { num: 5, name: "Ramy Bensebaini", pos: "CB" },
          { num: 6, name: "Houssem Aouar", pos: "CB" },
          { num: 3, name: "Youcef Atal", pos: "LB" },
          { num: 8, name: "Nabil Bentaleb", pos: "CM" },
          { num: 13, name: "Ismaël Bennacer", pos: "CM" },
          { num: 14, name: "Sofiane Feghouli", pos: "RW" },
          { num: 10, name: "Riyad Mahrez", pos: "AM" },
          { num: 11, name: "Yassine Brahimi", pos: "LW" },
          { num: 9, name: "Baghdad Bounedjah", pos: "ST" },
        ],
      },
    },
    timeline: [
      { minute: "KO", side: "neutral", type: "ko", text: "Kick-off at Mercedes-Benz Stadium." },
      { minute: "18'", side: "argentina", type: "goal", text: "Julián Álvarez finishes a Messi one-two." },
      { minute: "43'", side: "argentina", type: "goal", text: "Messi converts from the penalty spot." },
      { minute: "HT", side: "neutral", type: "ht", text: "Half-time: Argentina 2 — 0 Algeria." },
      { minute: "55'", side: "opponent", type: "goal", text: "Mahrez curls one in from the edge of the box." },
      { minute: "71'", side: "argentina", type: "goal", text: "Messi slots a low finish into the corner." },
      { minute: "FT", side: "neutral", type: "ft", text: "Full-time: Argentina 3 — 1 Algeria." },
    ],
    keyPlayer: {
      name: "Lionel Messi",
      role: "Captain · #10",
      note: "Two goals and an assist on opening night — the standard-setter from the first whistle.",
    },
    headToHead: { played: 5, arg: 3, draws: 1, opp: 1 },
  },

  austria: {
    relatedArticleSlug: "argentina-austria-group-clincher",
    lead:
      "A disciplined Austria held out for an hour at NRG Stadium before Lautaro Martínez and " +
      "Alexis Mac Allister broke the resistance to seal top spot in Group A.",
    storylines: [
      "Ralf Rangnick's mid-block frustrated Argentina's interior runners for the first half.",
      "Lautaro Martínez rewarded his start with the breakthrough on 63 minutes.",
      "Mac Allister capped the win nine minutes later from a Messi corner.",
    ],
    lineups: {
      argentina: argentinaLineup,
      opponent: {
        formation: "4-2-3-1",
        manager: "Ralf Rangnick",
        xi: [
          { num: 1, name: "Patrick Pentz", pos: "GK" },
          { num: 21, name: "Stefan Posch", pos: "RB" },
          { num: 4, name: "Kevin Danso", pos: "CB" },
          { num: 15, name: "Philipp Lienhart", pos: "CB" },
          { num: 16, name: "Maximilian Wöber", pos: "LB" },
          { num: 6, name: "Nicolas Seiwald", pos: "DM" },
          { num: 8, name: "Konrad Laimer", pos: "DM" },
          { num: 24, name: "Christoph Baumgartner", pos: "RW" },
          { num: 10, name: "Florian Grillitsch", pos: "AM" },
          { num: 19, name: "Christoph Trimmel", pos: "LW" },
          { num: 9, name: "Marko Arnautović", pos: "ST" },
        ],
      },
    },
    timeline: [
      { minute: "KO", side: "neutral", type: "ko", text: "Kick-off in Houston." },
      { minute: "HT", side: "neutral", type: "ht", text: "Half-time: Argentina 0 — 0 Austria." },
      { minute: "63'", side: "argentina", type: "goal", text: "Lautaro Martínez fires in a Tagliafico cut-back." },
      { minute: "72'", side: "argentina", type: "goal", text: "Mac Allister lashes home after a Messi corner." },
      { minute: "FT", side: "neutral", type: "ft", text: "Full-time: Argentina 2 — 0 Austria. Group A secured." },
    ],
    keyPlayer: {
      name: "Alexis Mac Allister",
      role: "Midfielder · #20",
      note: "Match-deciding goal and 91% pass completion against Rangnick's press.",
    },
    headToHead: { played: 7, arg: 4, draws: 1, opp: 2 },
  },

  jordan: {
    relatedArticleSlug: "argentina-jordan-friendly-tune-up",
    lead:
      "A pre-tournament friendly at the Estadio Monumental gives Scaloni one last 90 minutes to settle " +
      "his XI before the World Cup knockout rounds restart.",
    storylines: [
      "Final audition for the wing-back roles, with Molina and Acuña pushing for starts.",
      "Garnacho and Nico González in line for extended minutes off the bench.",
      "Jordan are unbeaten on the road in 2026 and will treat the match as a showcase.",
    ],
    lineups: {
      argentina: argentinaLineup,
      opponent: {
        formation: "4-4-2",
        manager: "Hussein Ammouta",
        xi: [
          { num: 1, name: "Yazeed Abulaila", pos: "GK" },
          { num: 2, name: "Mohammad Abu Hashish", pos: "RB" },
          { num: 5, name: "Yazan Al-Arab", pos: "CB" },
          { num: 4, name: "Abdallah Nasib", pos: "CB" },
          { num: 3, name: "Ihsan Haddad", pos: "LB" },
          { num: 7, name: "Noor Al-Rawabdeh", pos: "RM" },
          { num: 6, name: "Nour Al-Rawabdeh", pos: "CM" },
          { num: 8, name: "Mahmoud Al-Mardi", pos: "CM" },
          { num: 11, name: "Mousa Tamari", pos: "LM" },
          { num: 9, name: "Ali Olwan", pos: "ST" },
          { num: 17, name: "Yazan Al-Naimat", pos: "ST" },
        ],
      },
    },
    timeline: [
      { minute: "KO", side: "neutral", type: "ko", text: "Kick-off in Buenos Aires." },
      { minute: "22'", side: "argentina", type: "goal", text: "Di María opens the scoring with a curling left-footer." },
      { minute: "HT", side: "neutral", type: "ht", text: "Half-time: Argentina 1 — 0 Jordan." },
      { minute: "61'", side: "argentina", type: "sub", text: "Triple change — Garnacho, Enzo and Lautaro all introduced." },
      { minute: "78'", side: "argentina", type: "goal", text: "Lautaro Martínez heads in from a Molina cross." },
      { minute: "FT", side: "neutral", type: "ft", text: "Full-time. A tidy work-out before the World Cup restart." },
    ],
    keyPlayer: {
      name: "Ángel Di María",
      role: "Forward · #11",
      note: "A farewell-tour storyline — every cap from here counts.",
    },
    headToHead: { played: 2, arg: 2, draws: 0, opp: 0 },
  },

  mexico: {
    lead:
      "Argentina face Mexico in the World Cup group stage of the expanded 2026 tournament, where 48 teams " +
      "compete across Canada, Mexico and the United States in 12 groups of four.",
    storylines: [
      "Mexico are co-hosts, making every meeting with El Tri feel louder and more hostile in North America.",
      "The new 104-match format rewards depth — Scaloni's bench management matters from the opening week.",
      "Argentina's attacking structure around Messi and Álvarez remains the headline tactical question.",
    ],
    keyPlayer: {
      name: "Julián Álvarez",
      role: "Forward · #9",
      note: "Two goals and a man-of-the-match display in his first World Cup opener as a starter.",
    },
    headToHead: { played: 36, arg: 16, draws: 14, opp: 6 },
  },

  brazil: {
    lead:
      "The Superclásico de las Américas returns to the Monumental — a Conmebol Qualifier weighted with " +
      "rivalry, recent history and World Cup-defining momentum.",
    storylines: [
      "First Argentina–Brazil meeting since the 2022 World Cup cycle ended.",
      "Scaloni's midfield triangle versus Brazil's front three is the marquee match-up.",
      "Three points would all-but-secure Argentina's automatic qualification.",
    ],
    keyPlayer: {
      name: "Cristian Romero",
      role: "Centre-back · #13",
      note: "Tasked with shutting down Vinícius Jr. in transition — his duels will define the night.",
    },
    headToHead: { played: 113, arg: 41, draws: 27, opp: 45 },
  },

  uruguay: {
    lead:
      "Argentina edged a tense 2–1 win at the Centenario, twice coming from in front in a Conmebol " +
      "Qualifier defined by Uruguayan pressure and Albiceleste composure on the break.",
    storylines: [
      "Mac Allister was the game's outstanding midfielder, breaking lines with every carry.",
      "Messi delivered both assists despite being doubled up across the front line.",
      "A bruising 90 minutes — Scaloni rotated three starters before the hour mark.",
    ],
    keyPlayer: {
      name: "Alexis Mac Allister",
      role: "Midfielder · #20",
      note: "Two assists and 94% pass completion in hostile territory.",
    },
    headToHead: { played: 199, arg: 92, draws: 49, opp: 58 },
  },

  chile: {
    lead:
      "Argentina take on Chile in a marquee friendly at Soldier Field, Chicago — a North American " +
      "showcase that doubles as a pre-tournament audition.",
    storylines: [
      "First Argentina match on US soil since the Copa América final run.",
      "Lautaro Martínez expected to start ahead of Álvarez as Scaloni rotates.",
      "Chile arrive with a new coach and a generational rebuild well underway.",
    ],
    keyPlayer: {
      name: "Lautaro Martínez",
      role: "Forward · #22",
      note: "A chance to push for a starting role with Álvarez rested.",
    },
    headToHead: { played: 100, arg: 60, draws: 26, opp: 14 },
  },

  venezuela: {
    lead:
      "Argentina's U17s opened their South American campaign with a 3–0 win over Venezuela, controlling " +
      "the tempo from kick-off in Asunción.",
    storylines: [
      "Three different scorers — depth across the front line.",
      "A clean sheet in match one sets the tone for the group.",
      "The pressing trigger from midfield worked exactly as drilled.",
    ],
    keyPlayer: {
      name: "Claudio Echeverri",
      role: "Attacking midfielder",
      note: "Two assists and constant creation between the lines.",
    },
    headToHead: { played: 24, arg: 17, draws: 5, opp: 2 },
  },

  colombia: {
    lead:
      "Argentina's U20s welcome Colombia to La Plata in a South American Championship clash that should " +
      "decide top spot in the group.",
    storylines: [
      "A rematch of last year's continental final.",
      "Argentina's home crowd will turn La Plata into a cauldron.",
      "Both sides are unbeaten in qualification — a true marker game.",
    ],
    keyPlayer: {
      name: "Valentín Carboni",
      role: "Forward",
      note: "The standout creative talent of the tournament so far.",
    },
    headToHead: { played: 41, arg: 21, draws: 11, opp: 9 },
  },

  peru: {
    lead:
      "Argentina's U15s open their South American Championship at home to Peru — a first taste of " +
      "continental football for the next Albiceleste generation.",
    storylines: [
      "Coach is rotating in a fresh batch of academy graduates.",
      "Argentina unbeaten at U15 level in 14 matches.",
      "Peru bring a compact, physical setup that will test composure.",
    ],
    keyPlayer: {
      name: "Argentina captain",
      role: "Midfielder",
      note: "The first-choice playmaker in this age group's golden generation.",
    },
    headToHead: { played: 18, arg: 12, draws: 4, opp: 2 },
  },
};

export function getFixtureContent(fixture: FixtureLike): FixtureContent {
  const base = buildDefaultContent(fixture);
  const override = OVERRIDES[fixture.opponent.toLowerCase()];
  if (!override) return base;
  return {
    ...base,
    ...override,
    lineups: override.lineups ?? base.lineups,
    timeline: override.timeline ?? base.timeline,
    storylines: override.storylines ?? base.storylines,
  };
}
